import { create } from "zustand";
import { isDemoActive } from "./demoFlag";
import { isoDateToLocalDate, toLocalISODate } from "../utils";
import { taskScopeKeys } from "../taskScopes";
import { getTaskIdentityKey } from "../taskContext";
import { useVendStore } from "./store";

export const routeWeekBounds = (date: string) => {
  const start = isoDateToLocalDate(date);
  const offsetToMonday = (start.getDay() + 6) % 7;
  start.setDate(start.getDate() - offsetToMonday);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return {
    scheduledFrom: toLocalISODate(start),
    scheduledTo: toLocalISODate(end),
  };
};

export type RoutePlannerTaskRange = {
  startDate: string;
  endDate: string;
  includeVisitMetrics: boolean;
};

export const routePlannerTaskRanges = ({
  selectedDates,
  weekDates,
  comparisonDates,
}: {
  selectedDates: string[];
  weekDates: string[];
  comparisonDates: string[];
}): RoutePlannerTaskRange[] => {
  const ranges = new Map<string, RoutePlannerTaskRange>();
  const addRange = (dateKey: string, includeVisitMetrics: boolean) => {
    const { scheduledFrom, scheduledTo } = routeWeekBounds(dateKey);
    const key = `${scheduledFrom}..${scheduledTo}`;
    const existing = ranges.get(key);
    if (includeVisitMetrics || !existing) {
      ranges.set(key, {
        startDate: scheduledFrom,
        endDate: scheduledTo,
        includeVisitMetrics,
      });
    }
  };

  comparisonDates.forEach((dateKey) => addRange(dateKey, false));
  selectedDates.forEach((dateKey) => addRange(dateKey, true));
  weekDates.forEach((dateKey) => addRange(dateKey, true));
  return Array.from(ranges.values());
};

export const excludeCoveredRoutePlannerRanges = (
  ranges: RoutePlannerTaskRange[],
  coveringRanges: RoutePlannerTaskRange[],
) => {
  const coveredBounds = new Set(
    coveringRanges.map((range) => `${range.startDate}..${range.endDate}`),
  );
  return ranges.filter(
    (range) => !coveredBounds.has(`${range.startDate}..${range.endDate}`),
  );
};

export const resetRoutePlannerDayCache = () => {};

type PlannerState = {
  days: Record<string, string>;
  loading: boolean;
  error: string | null;
  reset: () => void;
  loadDay: (date: string) => Promise<void>;
  refreshDay: (date: string) => Promise<void>;
  refreshLoadedDaysSilent: () => Promise<void>;
  loadDaySilent: (date: string) => Promise<void>;
};

const loadTasksForDay = async (date: string, force = false) => {
  const { scheduledFrom, scheduledTo } = routeWeekBounds(date);
  const scopeKey = taskScopeKeys.dateRange(scheduledFrom, scheduledTo);
  if (!force && useVendStore.getState().taskScopes?.[scopeKey]) return;
  await useVendStore.getState().loadTasks({
    force,
    silent: true,
    filters: { scheduledFrom, scheduledTo },
    scopeKey,
  });
};

export const useRoutePlannerStore = create<PlannerState>((set, get) => {
  const reset = () => set({ days: {}, loading: false, error: null });

  const loadDayInternal = async (
    date: string,
    force = false,
    silent = false
  ) => {
    if (isDemoActive()) return;
    const taskIdentityKey = getTaskIdentityKey();
    if (!force && get().days[date] === taskIdentityKey) return;

    if (!silent) {
      set({ loading: true, error: null });
    } else {
      set((state) => ({ ...state, error: null }));
    }

    try {
      await loadTasksForDay(date, force);
      set((state) => ({
        ...state,
        days: { ...(state.days || {}), [date]: taskIdentityKey },
        loading: silent ? state.loading : false,
        error: null,
      }));
    } catch (e: any) {
      set((state) => ({
        ...state,
        loading: silent ? state.loading : false,
        error: e?.message || "Failed to load route day",
      }));
    }
  };

  return {
    days: {},
    loading: false,
    error: null,
    reset,
    loadDay: (date) => loadDayInternal(date, false, false),
    refreshDay: (date) => loadDayInternal(date, true, false),
    refreshLoadedDaysSilent: async () => {
      if (isDemoActive()) return;
      const dayKeys = Object.keys(get().days || {});
      if (!dayKeys.length) return;
      await Promise.allSettled(
        dayKeys.map((dayKey) => loadDayInternal(dayKey, true, true))
      );
    },
    loadDaySilent: (date) => loadDayInternal(date, false, true),
  };
});
